import { AlertCircle } from 'lucide-react';
import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router';

import { SnapshotDraftOverlay } from '@/components/snapshot/snapshot-panel';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CarmEditPanelManager } from '@/components/viewer/carm-edit-panel-manager';
import { ErrorBoundary } from '@/components/viewer/error-boundary';
import { LoadingProgressDialog } from '@/components/viewer/loading-progress-dialog';
import { PanelManager } from '@/components/viewer/panel-manager';
import { SliderManager } from '@/components/viewer/slider-manager';
import { StepSidebar } from '@/components/viewer/step-sidebar';
import { ViewerHeader } from '@/components/viewer/viewer-header';
import { ViewerLayout } from '@/components/viewer/viewer-layout';
import { ViewportContainer } from '@/components/viewer/viewport-container';
import { ViewportToolbarManager } from '@/components/viewer/viewport-toolbar-manager';
import { WindowLevelIndicatorManager } from '@/components/viewer/window-level/window-level-indicator-manager';
import { useTenant, VisualizationProvider, WorkflowStepProvider } from '@/context';
import { useSnapshotPersistence } from '@/hooks/use-snapshot-persistence';
import { useViewerLayout } from '@/hooks/use-viewer-layout';
import type { LoadProgressPayload } from '@/visualization';
import { useViewerSession } from '@/workflows/common/hooks/use-viewer-session';
import { useWorkflowFeatureVisibility } from '@/workflows/common/hooks/use-workflow-feature-visibility';
import type { WorkflowConfig } from '@/workflows/types';

/**
 * Viewer page for displaying case visualization data.
 * Only needs caseId — all orchestration is handled by useViewerSession.
 */
export function ViewerPage() {
    const { caseId } = useParams<{ caseId: string }>();
    const { tenantId } = useTenant();
    const navigate = useNavigate();
    const handleBack = () => navigate(`/tenants/${tenantId}`);

    if (!caseId) return <ErrorView error={new Error('No case ID provided')} />;

    return (
        <VisualizationProvider>
            <ViewerSession caseId={caseId} onBack={handleBack} />
        </VisualizationProvider>
    );
}

/**
 * Manages the viewer session lifecycle via useViewerSession.
 * Gates WorkflowStepProvider on config availability (requires async case fetch).
 */
function ViewerSession({ caseId, onBack }: { caseId: string; onBack: () => void }) {
    const { status, config, progress, error } = useViewerSession(caseId);

    // Hook must be called unconditionally (rules of hooks); value is unused when config is null
    const validStepIds = useMemo(() => new Set(config?.steps.map((s) => s.id)), [config]);

    if (status === 'initializing' || !config) return <ViewerSkeleton />;
    if (status === 'error' && error) return <ErrorView error={error} />;

    return (
        <WorkflowStepProvider defaultStepId={config.defaultStepId} validStepIds={validStepIds}>
            <ViewerContent
                caseId={caseId}
                config={config}
                isLoading={status === 'loading'}
                isReady={status === 'ready'}
                progress={progress}
                loadError={status === 'error' ? error : null}
                onBack={onBack}
            />
        </WorkflowStepProvider>
    );
}

/**
 * Inner content that uses visualization services and workflow step context.
 * Must be wrapped in VisualizationProvider and WorkflowStepProvider.
 * Loading state arrives as props — no data-fetching hooks here.
 */
interface ViewerContentProps {
    caseId: string;
    config: WorkflowConfig;
    isLoading: boolean;
    isReady: boolean;
    progress: LoadProgressPayload;
    loadError: Error | null;
    onBack: () => void;
}

function ViewerContent({
    caseId,
    config,
    isLoading,
    isReady,
    progress,
    loadError,
    onBack,
}: ViewerContentProps) {
    // Layout management driven by active workflow step
    const { currentLayout, availableLayouts, setLayout, activeStep, isComponentStep } =
        useViewerLayout(config);

    // Apply declarative feature visibility rules (no-op when config has none)
    useWorkflowFeatureVisibility(config.featureVisibility ?? []);

    // Mirror snapshot store mutations to the server (best-effort, fire-and-forget).
    useSnapshotPersistence({ caseId });

    // Render headerExtra from workflow config
    const HeaderExtra = config.headerExtra;
    const headerExtra = HeaderExtra ? <HeaderExtra caseId={caseId} /> : undefined;

    // Render sidebar from workflow steps
    const sidebar = (
        <aside className="hidden w-80 shrink-0 border-r border-border bg-muted/30 md:block">
            <StepSidebar steps={config.steps} />
        </aside>
    );

    // Show loading dialog while data loads
    if (isLoading) {
        return (
            <ViewerLayout
                header={
                    <ViewerHeader
                        currentLayout={currentLayout}
                        availableLayouts={availableLayouts}
                        onLayoutChange={setLayout}
                        onBack={onBack}
                        isComponentStep={isComponentStep}
                    />
                }
                sidebar={sidebar}
            >
                <LoadingProgressDialog open={true} progress={progress} onCancel={onBack} />
            </ViewerLayout>
        );
    }

    // Show error view
    if (loadError) {
        return (
            <ViewerLayout
                header={
                    <ViewerHeader
                        currentLayout={currentLayout}
                        availableLayouts={availableLayouts}
                        onLayoutChange={setLayout}
                        onBack={onBack}
                        isComponentStep={isComponentStep}
                    />
                }
                sidebar={sidebar}
            >
                <ErrorView error={loadError} />
            </ViewerLayout>
        );
    }

    // Resolve the component for component-based steps
    const MainViewComponent = activeStep.mainView.type === 'component' ? activeStep.mainView.component : null;

    // Show loaded viewer
    if (isReady && (currentLayout || isComponentStep)) {
        return (
            <ViewerLayout
                header={
                    <ViewerHeader
                        currentLayout={currentLayout}
                        availableLayouts={availableLayouts}
                        onLayoutChange={setLayout}
                        onBack={onBack}
                        extra={headerExtra}
                        isComponentStep={isComponentStep}
                    />
                }
                sidebar={sidebar}
            >
                {/* VTK viewports — always mounted, hidden when a component step is active */}
                <div className={isComponentStep ? 'hidden' : 'contents'}>
                    {currentLayout && <ViewportContainer layout={currentLayout} />}
                    <ViewportToolbarManager />
                    <WindowLevelIndicatorManager />
                    <SliderManager />
                    <CarmEditPanelManager />
                    <PanelManager panels={config.panels} />
                </div>
                {/* Component step main view — wrapped in ErrorBoundary to prevent blank screen on render errors */}
                {isComponentStep && MainViewComponent && (
                    <ErrorBoundary
                        fallback={
                            <ErrorView
                                error={new Error('This view failed to render. Please try reloading.')}
                            />
                        }
                    >
                        <MainViewComponent />
                    </ErrorBoundary>
                )}
                {/* Capture preview/annotation flow — global so screenshots taken from any
                    viewport surface here regardless of the active step. */}
                <SnapshotDraftOverlay />
            </ViewerLayout>
        );
    }

    // Fallback — reached when isReady is false but no error was set (should not happen normally)
    console.error('[ViewerContent] Unexpected state: not loading, no error, but not ready', {
        caseId,
        workflowId: config.workflowId,
        isReady,
        isLoading,
        loadError,
    });
    return (
        <ViewerLayout
            header={
                <ViewerHeader
                    currentLayout={currentLayout}
                    availableLayouts={availableLayouts}
                    onLayoutChange={setLayout}
                    onBack={onBack}
                />
            }
            sidebar={sidebar}
        >
            <Card className="m-4 border-yellow-500/50 bg-yellow-500/10">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-yellow-500" />
                        No Data Available
                    </CardTitle>
                    <CardDescription>No visualization data is available for this case.</CardDescription>
                </CardHeader>
            </Card>
        </ViewerLayout>
    );
}

/**
 * Error view shown when loading fails.
 */
interface ErrorViewProps {
    error: Error;
}

function ErrorView({ error }: ErrorViewProps) {
    return (
        <div className="flex h-full items-center justify-center p-4">
            <Card className="max-w-md border-destructive/50 bg-destructive/10">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-destructive">
                        <AlertCircle className="h-5 w-5" />
                        Error Loading Visualization
                    </CardTitle>
                    <CardDescription className="text-destructive/80">{error.message}</CardDescription>
                </CardHeader>
            </Card>
        </div>
    );
}

/**
 * Skeleton loading state for the viewer page.
 */
function ViewerSkeleton() {
    return (
        <div className="flex h-screen flex-col">
            {/* Header skeleton */}
            <div className="flex h-14 items-center justify-between border-b px-4">
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-8 w-40" />
            </div>
            {/* Content skeleton */}
            <div className="flex flex-1">
                <div className="hidden w-60 border-r md:block">
                    <div className="p-4 space-y-4">
                        <Skeleton className="h-6 w-20" />
                        <Skeleton className="h-4 w-32" />
                        <div className="space-y-2 pt-4">
                            <Skeleton className="h-8 w-full" />
                            <Skeleton className="h-8 w-full" />
                            <Skeleton className="h-8 w-full" />
                        </div>
                    </div>
                </div>
                <div className="flex-1 p-4">
                    <Skeleton className="h-full w-full rounded-lg" />
                </div>
            </div>
        </div>
    );
}
